import { useState } from 'react'
import {
  ChevronRight,
  ChevronDown,
  Table as TableIcon,
  Scan,
  AlertTriangle,
  BotMessageSquare,
  FileText,
  Loader2,
  Shield,
  Eye,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ClassificationBadge } from '@/components/classifications'
import { useDataTableColumns } from '@/hooks/use-data-tables'
import { cn } from '@/lib/utils'
import { SampleValuesPopover } from './sample-values-popover'
import type { DataTable, DataColumn } from '@/types/data-table'

interface DataTableBrowserProps {
  tables: DataTable[]
  onScanTable?: (table: DataTable) => void
  onDetectSensitive?: (table: DataTable) => void
}

/**
 * DataTableBrowser displays data tables with expandable column lists
 */
export function DataTableBrowser({
  tables,
  onScanTable,
  onDetectSensitive,
}: DataTableBrowserProps) {
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set())

  const toggleTable = (tableId: string) => {
    const newExpanded = new Set(expandedTables)
    if (newExpanded.has(tableId)) {
      newExpanded.delete(tableId)
    } else {
      newExpanded.add(tableId)
    }
    setExpandedTables(newExpanded)
  }

  if (tables.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No data tables found
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {tables.map((table) => (
        <DataTableItem
          key={table.id}
          table={table}
          isExpanded={expandedTables.has(table.id)}
          onToggle={() => toggleTable(table.id)}
          onScanTable={onScanTable}
          onDetectSensitive={onDetectSensitive}
        />
      ))}
    </div>
  )
}

interface DataTableItemProps {
  table: DataTable
  isExpanded: boolean
  onToggle: () => void
  onScanTable?: (table: DataTable) => void
  onDetectSensitive?: (table: DataTable) => void
}

function DataTableItem({
  table,
  isExpanded,
  onToggle,
  onScanTable,
  onDetectSensitive,
}: DataTableItemProps) {
  const navigate = useNavigate()

  const handleAskAI = (e: React.MouseEvent) => {
    e.stopPropagation()
    const params = new URLSearchParams({
      context: 'data-catalog',
      tableId: table.id,
      tableName: `${table.schemaName}.${table.tableName}`,
    })
    navigate(`/polix-ai?${params.toString()}`)
  }

  const handleApplyTemplate = (e: React.MouseEvent) => {
    e.stopPropagation()
    const params = new URLSearchParams({
      source: 'data-catalog',
      resourceType: `${table.schemaName}.${table.tableName}`,
    })
    navigate(`/pac/policies/new?${params.toString()}`)
  }

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Table header */}
      <div
        className={cn(
          'flex items-center gap-3 px-4 py-3 hover:bg-accent transition-colors cursor-pointer',
          isExpanded && 'bg-accent/50',
        )}
        onClick={onToggle}
      >
        {/* Expand/collapse icon */}
        <button
          type="button"
          className="shrink-0"
          onClick={(e) => {
            e.stopPropagation()
            onToggle()
          }}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {/* Table icon */}
        <TableIcon className="h-5 w-5 shrink-0 text-muted-foreground" />

        {/* Table name */}
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{table.displayName}</div>
          <div className="text-sm text-muted-foreground truncate">
            {table.schemaName}.{table.tableName}
          </div>
        </div>

        {/* Sync source badge */}
        {table.syncSource === 'DISCOVERY' && (
          <Badge variant="outline" className="text-xs">
            Discovery
          </Badge>
        )}

        {/* Classification badge */}
        {table.classification && (
          <ClassificationBadge
            level={table.classification.level as any}
            customColor={table.classification.color}
          />
        )}

        {/* Metadata */}
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          {table.rowCount !== null && table.rowCount !== undefined && (
            <span>{table.rowCount.toLocaleString()} rows</span>
          )}
          {table._count?.columns !== undefined && (
            <Badge variant="secondary">{table._count.columns} columns</Badge>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={handleAskAI} title="Create Policy with AI">
            <BotMessageSquare className="h-4 w-4 mr-1" />
            Ask AI
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleApplyTemplate}
            title="Apply Policy Template"
          >
            <FileText className="h-4 w-4 mr-1" />
            Template
          </Button>
          {onScanTable && (
            <Button variant="ghost" size="sm" onClick={() => onScanTable(table)}>
              <Scan className="h-4 w-4 mr-1" />
              Scan
            </Button>
          )}
          {onDetectSensitive && (
            <Button variant="ghost" size="sm" onClick={() => onDetectSensitive(table)}>
              <AlertTriangle className="h-4 w-4 mr-1" />
              Detect
            </Button>
          )}
        </div>
      </div>

      {/* Table details (expanded) */}
      {isExpanded && (
        <div className="border-t bg-muted/30">
          {/* Description */}
          {table.description && (
            <div className="px-4 py-3 border-b">
              <p className="text-sm text-muted-foreground">{table.description}</p>
            </div>
          )}

          {/* Metadata row */}
          <div className="px-4 py-2 border-b text-sm flex items-center gap-4 flex-wrap">
            {table.lastScanned && (
              <span>
                <span className="text-muted-foreground">Last scanned:</span>{' '}
                <span className="font-medium">
                  {new Date(table.lastScanned).toLocaleString()}
                </span>
              </span>
            )}
            {table.syncedAt && (
              <span>
                <span className="text-muted-foreground">Synced:</span>{' '}
                <span className="font-medium">
                  {new Date(table.syncedAt).toLocaleString()}
                </span>
              </span>
            )}
            {table.discoveryConnectionName && (
              <span>
                <span className="text-muted-foreground">Connection:</span>{' '}
                <span className="font-medium">{table.discoveryConnectionName}</span>
              </span>
            )}
          </div>

          {/* Columns loaded on expand */}
          <DataTableExpandedColumns tableId={table.id} tableName={`${table.schemaName}.${table.tableName}`} />
        </div>
      )}
    </div>
  )
}

/**
 * Lazy-loaded column list that fetches when the table is expanded
 */
function DataTableExpandedColumns({
  tableId,
  tableName,
}: {
  tableId: string
  tableName: string
}) {
  const navigate = useNavigate()
  const { data: columns, isLoading, isError } = useDataTableColumns(tableId)

  if (isLoading) {
    return (
      <div className="px-4 py-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading columns...
      </div>
    )
  }

  if (isError) {
    return (
      <div className="px-4 py-3 text-sm text-destructive">Failed to load columns</div>
    )
  }

  if (!columns || columns.length === 0) {
    return (
      <div className="px-4 py-3 text-sm text-muted-foreground">No columns found</div>
    )
  }

  const classificationCounts = columns.reduce<Record<string, { count: number; color?: string }>>((acc, col) => {
    const level = col.classification?.level ?? 'Unclassified'
    const existing = acc[level]
    return {
      ...acc,
      [level]: {
        count: (existing?.count ?? 0) + 1,
        color: col.classification?.color ?? existing?.color,
      },
    }
  }, {})

  const handleColumnAskAI = (column: DataColumn) => {
    const params = new URLSearchParams({
      context: 'data-catalog',
      tableId,
      tableName,
      columnName: column.columnName,
      classificationLevel: column.classification?.level ?? '',
    })
    navigate(`/polix-ai?${params.toString()}`)
  }

  return (
    <div className="divide-y">
      {Object.keys(classificationCounts).length > 0 && (
        <div className="px-4 py-2 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Classification:</span>
          {Object.entries(classificationCounts).map(([level, { count, color }]) => (
            <Badge
              key={level}
              variant="outline"
              className="text-xs"
              style={color ? { borderColor: color, color } : undefined}
            >
              {count} {level}
            </Badge>
          ))}
        </div>
      )}
      {columns.map((col) => (
        <div
          key={col.id}
          className="px-4 py-2 flex items-center gap-3 text-sm hover:bg-accent/30"
        >
          {/* Column name */}
          <code className="font-mono text-xs flex-shrink-0 min-w-[120px]">
            {col.columnName}
          </code>

          {/* Data type */}
          <Badge variant="outline" className="text-xs font-mono">
            {col.dataType}
          </Badge>

          {/* Nullable badge */}
          {col.isNullable && (
            <Badge variant="outline" className="text-xs">
              nullable
            </Badge>
          )}

          {/* Classification */}
          {col.classification && (
            <ClassificationBadge
              level={col.classification.level as any}
              customColor={col.classification.color}
            />
          )}

          {/* Critical flag */}
          {col.isCritical && (
            <Badge variant="destructive" className="text-xs">
              <Shield className="h-3 w-3 mr-1" />
              Critical
            </Badge>
          )}

          {/* Regulatory flags */}
          {col.regulatoryFlags && col.regulatoryFlags.length > 0 && (
            <div className="flex gap-1">
              {col.regulatoryFlags.map((flag) => (
                <Badge key={flag} variant="secondary" className="text-xs">
                  {flag}
                </Badge>
              ))}
            </div>
          )}

          {/* Detected patterns */}
          {col.detectedPatterns && col.detectedPatterns.length > 0 && (
            <div className="flex gap-1">
              {col.detectedPatterns.map((pattern) => (
                <Badge key={pattern} variant="outline" className="text-xs">
                  {pattern.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          )}

          {/* Classification method */}
          {col.classificationMethod && (
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                col.classificationMethod === 'RULE_BASED' && 'border-blue-500 text-blue-600',
                col.classificationMethod === 'AI_ASSISTED' && 'border-purple-500 text-purple-600',
                col.classificationMethod === 'MANUAL' && 'border-green-500 text-green-600',
              )}
            >
              {col.classificationMethod === 'RULE_BASED' ? 'Rule' : col.classificationMethod === 'AI_ASSISTED' ? 'AI' : 'Manual'}
            </Badge>
          )}

          {/* Requires review */}
          {col.requiresReview && (
            <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">
              <Eye className="h-3 w-3 mr-1" />
              Needs Review
            </Badge>
          )}

          {/* Confidence */}
          {col.confidence !== undefined && col.confidence !== null && (
            <span className="text-xs text-muted-foreground">
              {Math.round(col.confidence * 100)}%
            </span>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Column actions */}
          <div className="flex items-center gap-1">
            <SampleValuesPopover columnId={col.id} columnName={col.columnName} />
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => handleColumnAskAI(col)}
            >
              <BotMessageSquare className="h-3 w-3 mr-1" />
              Ask AI
            </Button>
          </div>
        </div>
      ))}
    </div>
  )
}
